import { ReactNode, createContext, useEffect, useState } from "react";

import { ISingleOrder } from "pages/[tenant]/orders/@types/sigle-order";

import api from "shared/infra/services/api";

import { useQuery } from "@tanstack/react-query";

import { useOrderQueryClientQueryKeys } from "../hooks/useOrderQueryClientQueryKeys";
import { CardDetailsProps, OrdersContextProps } from "./types";

async function getOrderRequest(options: { uuid: string; status: string }) {
  try {
    const queryParams: any = {};
    const { status, uuid } = options;

    if (status) {
      queryParams["where[0][index]"] = "status";
      queryParams["where[0][condition]"] = "like";
      queryParams["where[0][value]"] = status;
    }

    if (uuid) {
      queryParams["where[1][index]"] = "uuid";
      queryParams["where[1][condition]"] = "like";
      queryParams["where[1][value]"] = uuid;
    }

    const response = await api.get("pay/order", { params: queryParams });
    return response.data.attrs;
  } catch (error) {
    console.log("error -->", error);
  }
  return null;
}

interface OrdersProviderProps {
  children: ReactNode;
  initialOrder?: ISingleOrder;
}

const OrdersContext = createContext({} as OrdersContextProps);

async function fetchCardInformation({ queryKey }: { queryKey }) {
  try {
    const [, _, cardId] = queryKey;
    if (!cardId) return null;
    const { data } = await api.get(`pay/card/${cardId}`);
    return data?.attrs;
  } catch (error) {
    return null;
  }
}

export function OrdersProvider({
  children,
  initialOrder,
}: OrdersProviderProps) {
  const { SINGLE_ORDER_QUERY_KEY } = useOrderQueryClientQueryKeys();
  const [order, setOrder] = useState<ISingleOrder>(initialOrder);

  const transactions = order?.transactions?.[0];

  const paymentType = transactions?.items?.payment_type;
  const accessPaymentMethod = transactions?.items?.access_payment_method;
  const orderPaymentMethod = transactions?.payment_method;
  const cardInformation = transactions?.items?.card_data;
  const accessCardInformation = transactions?.items?.access_card;

  const paymentMethodInitialState =
    paymentType === "recurrent" && !accessCardInformation
      ? accessPaymentMethod
      : orderPaymentMethod;

  const cardDataInitialState =
    paymentType === "recurrent" && !accessCardInformation
      ? accessCardInformation
      : cardInformation;

  const [filterOn, setFilterOn] = useState(false);
  const [loaderFilter, setLoaderFilter] = useState(false);
  const [dataFiltered, setDataFiltered] = useState(null);
  const [cardData, setCardData] =
    useState<CardDetailsProps>(cardDataInitialState);

  const [addNewCardOpen, setAddNewCardOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethodInitialState);

  const [selectedMethod, setSelectedMethod] = useState();
  const [cards, setCards] = useState();
  const [selectedCard, setSelectedCard] = useState(null);

  const [openChangePaymentMethodModal, setOpenChangePaymentMethodModal] =
    useState(false);

  const [openChangePaymentMethodSuccess, setOpenChangePaymentMethodSuccess] =
    useState(false);

  const [isRefundRequestModalOpen, setIsRefundRequestModalOpen] =
    useState(false);

  const [isCancelRecurrenceModalOpen, setIsCancelRecurrenceModalOpen] =
    useState(false);

  const [
    openSuccessRecurrenceCancelationModal,
    setOpenSuccessRecurrenceCancelationModal,
  ] = useState(false);

  const onSearch = async (options: { uuid: string; status: string }) => {
    setFilterOn(true);
    setLoaderFilter(true);
    const data = await getOrderRequest(options);
    setDataFiltered(data);
    setLoaderFilter(false);
  };

  const onClearFilter = ({ setFilterValue, setStatus }: any) => {
    setFilterOn(false);
    if (setFilterValue) setFilterValue("");
    if (setStatus) setStatus("all");
  };

  const { data: creditCard } = useQuery({
    queryKey: [...SINGLE_ORDER_QUERY_KEY, order?.access?.card_id],
    queryFn: fetchCardInformation,
    refetchOnWindowFocus: false,
    initialData: paymentMethodInitialState,
  });

  useEffect(
    function updateCreditCardDataOnPageLoad() {
      setCardData(creditCard);
    },
    [setCardData, creditCard],
  );

  return (
    <OrdersContext.Provider
      value={{
        onSearch,
        filterOn,
        loaderFilter,
        dataFiltered,
        onClearFilter,

        addNewCardOpen,
        setAddNewCardOpen,

        cards,
        setCards,

        selectedMethod,
        setSelectedMethod,

        openChangePaymentMethodModal,
        setOpenChangePaymentMethodModal,

        selectedCard,
        setSelectedCard,

        openChangePaymentMethodSuccess,
        setOpenChangePaymentMethodSuccess,

        openSuccessRecurrenceCancelationModal,
        setOpenSuccessRecurrenceCancelationModal,

        isCancelRecurrenceModalOpen,
        setIsCancelRecurrenceModalOpen,

        paymentMethod,
        setPaymentMethod,

        cardData,
        setCardData,

        isRefundRequestModalOpen,
        setIsRefundRequestModalOpen,

        order,
        setOrder,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}

export default OrdersContext;
